// The slice of a RIFT-WebTransport session the guest client uses, and the
// adapter onto the vendored rift-wt bundle (vendor/rift-wt, built from the
// third_party/rift pin — see vendor/rift-wt/PIN.json).
//
// Flows (participant-loop-contracts §2.2): 1 uplink, 2 return feed, 3 RTT
// JSON, 4 the gateway's control messages (v2: `{"kind":"keyframeRequest"}`
// when the guest is routed onto a slot). Receiving flows 2 and 4 and sending
// flow 3 need Jeket-com/rift#28 (vendored at 8124cfc): `openReceiveFlow(id)`,
// `openFlow(id, label)` and `sendFrameOnFlow(id, bytes, key)`. Every open
// must happen before `beginSession()` so the flows are in the Hello. Older
// bundles (e.g. 066ca29) have a single sender flow; the adapter
// feature-detects the API and reports `bidirectional: false` for them, so the
// client publishes flow 1 only and expects neither a return feed nor control
// messages, and sends no RTT reports.
export const FLOW_UPLINK = 1;
export const FLOW_RETURN = 2;
export const FLOW_RTT = 3;
export const FLOW_CONTROL = 4;
class RiftWtSessionAdapter {
    native;
    bidirectional;
    constructor(native, bidirectional) {
        this.native = native;
        this.bidirectional = bidirectional;
    }
    beginSession() {
        return this.native.beginSession();
    }
    sendFrame(payload, keyframe) {
        return this.native.sendFrame(payload, keyframe);
    }
    sendOnFlow(flow, payload) {
        // Frame-aligned when the bundle can: plain sendOnFlow appends to the
        // current chunk and flushes only when it fills, which would hold a small
        // RTT report back indefinitely.
        if (this.native.sendFrameOnFlow)
            return this.native.sendFrameOnFlow(flow, payload, false);
        return this.native.sendOnFlow(flow, payload);
    }
    nextEvent() {
        return this.native.nextEvent();
    }
    recommendedBitrateBps() {
        return this.native.recommendedBitrateBps();
    }
    async shutdown() {
        try {
            await this.native.shutdown();
        }
        finally {
            try {
                this.native.free();
            }
            catch {
                /* already freed */
            }
        }
    }
}
/**
 * Connector over the vendored rift-wt bundle. `load` imports the module; it
 * is injectable so an app can serve the bundle from its own asset pipeline
 * (the orchestrator frontend passes `() => import('@jeket-cloud/rift-guest-client/rift-wt')`).
 */
export function riftWtConnector(load) {
    let ready = null;
    const module = () => {
        ready ??= load().then(async (m) => {
            const mod = m;
            await mod.default();
            return mod;
        });
        return ready;
    };
    return async (opts) => {
        const mod = await module();
        const o = new mod.RiftWtOptions(opts.url, FLOW_UPLINK);
        o.setFlowLabel(opts.flowLabel ?? 'guest-uplink-ts');
        o.setAuthToken(opts.token);
        o.setChunkSize(opts.chunkSizeBytes ?? 20_000);
        for (const h of opts.certHashes ?? [])
            o.addCertHash(h);
        const native = await mod.RiftWtSession.connect(o);
        // Before beginSession (the caller's next step): flows opened later are
        // not in the Hello, and the peer drops their chunks.
        let bidirectional = false;
        if (typeof native.openFlow === 'function' && typeof native.openReceiveFlow === 'function') {
            await native.openReceiveFlow(FLOW_RETURN);
            await native.openReceiveFlow(FLOW_CONTROL);
            await native.openFlow(FLOW_RTT, 'guest-rtt-json');
            bidirectional = true;
        }
        return new RiftWtSessionAdapter(native, bidirectional);
    };
}
//# sourceMappingURL=session.js.map